import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';

import dayjs, { Duration } from '../../../core/configs/dayjs';
import { SubscriptionStatus } from '../../domain/subscription.types';

export type SubscriptionHistory = {
	id: number;
	status: SubscriptionStatus;
	action: string;
	duration: Duration | null;
	columnCount: number;
	lineCount: number;
	date: dayjs.Dayjs | null;
	avatar: AvatarEntity | null;
};

interface IPublishedSubscriptionEntity {
	id: number;
	title: string;
	details: string;
	keywords: Array<AppTagItemEntity>;
	status: SubscriptionStatus;
	isInSync: boolean;
	datablockId: number;
	datablockLabel: string;
	datablockUrl: string;
	publishedDate: dayjs.Dayjs | null;
	publishedBy: AvatarEntity | null;
	dataCount: number;
	columnCount: number;
	history: Array<SubscriptionHistory>;
}

const entityDefaults: IPublishedSubscriptionEntity = {
	id: -1,
	title: '',
	details: '',
	keywords: [],
	status: SubscriptionStatus.Unknown,
	isInSync: false,
	datablockId: -1,
	datablockLabel: '',
	datablockUrl: '',
	publishedDate: null,
	publishedBy: null,
	dataCount: 0,
	columnCount: 0,
	history: [],
};

export class PublishedSubscriptionEntity
	implements IPublishedSubscriptionEntity
{
	private [immerable] = true;
	public id = entityDefaults.id;
	public title = entityDefaults.title;
	public details = entityDefaults.details;
	public keywords = entityDefaults.keywords;
	public status = entityDefaults.status;
	public isInSync = entityDefaults.isInSync;
	public datablockId = entityDefaults.datablockId;
	public datablockLabel = entityDefaults.datablockLabel;
	public datablockUrl = entityDefaults.datablockUrl;
	public publishedDate = entityDefaults.publishedDate;
	public publishedBy = entityDefaults.publishedBy;
	public dataCount = entityDefaults.dataCount;
	public columnCount = entityDefaults.columnCount;
	public history = entityDefaults.history;

	public static build(
		params: Partial<IPublishedSubscriptionEntity> = {}
	): PublishedSubscriptionEntity {
		const instance = new PublishedSubscriptionEntity();
		instance.id = params.id ?? entityDefaults.id;
		instance.title = params.title ?? entityDefaults.title;
		instance.details = params.details ?? entityDefaults.details;
		instance.keywords = params.keywords ?? entityDefaults.keywords;
		instance.status = params.status ?? entityDefaults.status;
		instance.isInSync = params.isInSync ?? entityDefaults.isInSync;
		instance.datablockId = params.datablockId ?? entityDefaults.datablockId;
		instance.datablockLabel =
			params.datablockLabel ?? entityDefaults.datablockLabel;
		instance.datablockUrl = params.datablockUrl ?? entityDefaults.datablockUrl;
		instance.publishedDate =
			params.publishedDate ?? entityDefaults.publishedDate;
		instance.publishedBy = params.publishedBy ?? entityDefaults.publishedBy;
		instance.dataCount = params.dataCount ?? entityDefaults.dataCount;
		instance.columnCount = params.columnCount ?? entityDefaults.columnCount;
		instance.history = params.history ?? entityDefaults.history;
		return instance;
	}

	public setIsInSync(isInSync: boolean): PublishedSubscriptionEntity {
		return produce(this, (draft) => {
			draft.isInSync = isInSync;
		});
	}

	public markAsWaiting(): PublishedSubscriptionEntity {
		return produce(this, (draft: PublishedSubscriptionEntity) => {
			const up = Array.from(this.history.slice(0, 2));
			up.unshift({
				id: -1,
				status: SubscriptionStatus.Waiting,
				action: '',
				duration: null,
				columnCount: 0,
				lineCount: 0,
				date: dayjs(),
				avatar: null,
			});
			draft.history = up;
			draft.status = SubscriptionStatus.Waiting;
		});
	}
}
